/**
 * Grouped search-results presentation for the add-players drawer (SE-6948,
 * PC2).
 *
 * Caption "Results across ALL players", then up to three groups: ALREADY IN
 * THIS PIPELINE (muted "In list" rows, no Add) · RECENTLY VIEWED · ALL
 * PLAYERS · N MATCHES. Rows carry **Add only — no Dismiss**: a searched
 * player has no stated recommendation reason, and there is nothing to hide
 * from a search result.
 * @remarks View component — no business logic, hooks, or state.
 */

import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import React, { memo } from "react";
import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import { Box } from "@/components/atoms/Box";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import type { PlayerSearchPlayerFragment } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

import {
  formatCandidateGpr,
  formatCandidateName,
  formatSearchMeta,
} from "../../../utils/addPlayersRowMeta";
import CompactPlayerRow from "../../molecules/AddPlayersRow/CompactPlayerRow";

/**
 * Render one result group's header + rows. A render helper (the
 * `renderSelectionChipRow` precedent), not a second component. Returns `null`
 * when the group has no rows — an empty header with nothing under it would
 * read as a loading failure.
 * @param groupKey - The group's test-identity key.
 * @param title - The group's header text.
 * @param players - The group's rows.
 * @param isInList - Whether the rows are muted in-pipeline rows.
 * @param addingPlayerIds - Ids with an add in flight.
 * @param onAdd - Add callback.
 * @returns The group subtree, or `null` when empty.
 */
const renderResultGroup = (
  groupKey: string,
  title: string,
  players: readonly PlayerSearchPlayerFragment[],
  isInList: boolean,
  addingPlayerIds: readonly string[],
  onAdd: (playerId: string) => void
): React.ReactElement | null =>
  players.length === 0 ? null : (
    <Stack className="gap-2">
      <Text
        variant="micro-strong"
        testID={TEST_ID_ADD_PLAYERS_PANEL.SEARCH_GROUP(groupKey)}
        className="text-content-muted"
      >
        {title}
      </Text>
      <Stack className="gap-2">
        {players.map(player => (
          <CompactPlayerRow
            key={player.id}
            playerId={player.id}
            name={formatCandidateName(player)}
            meta={formatSearchMeta(player)}
            gpr={formatCandidateGpr(player)}
            isInList={isInList}
            isAdding={addingPlayerIds.includes(player.id)}
            onAdd={isInList ? undefined : onAdd}
          />
        ))}
      </Stack>
    </Stack>
  );

/** Props for {@link AddPlayersSearchResultsView}. */
interface AddPlayersSearchResultsViewProps {
  /** Matches already placed on this pipeline. */
  readonly inPipeline: readonly PlayerSearchPlayerFragment[];
  /** Matches the user recently interacted with. */
  readonly recentlyViewed: readonly PlayerSearchPlayerFragment[];
  /** Every other match. */
  readonly others: readonly PlayerSearchPlayerFragment[];
  /** The ALL PLAYERS group's match count. */
  readonly matchCount: number;
  /** Whether the first page of results is loading. */
  readonly isLoading: boolean;
  /** Whether a completed search matched nothing. */
  readonly isEmpty: boolean;
  /** Search error, or `null`. */
  readonly error: string | null;
  /** Player ids with an add in flight. */
  readonly addingPlayerIds: readonly string[];
  /** Add a player, by id. */
  readonly onAdd: (playerId: string) => void;
  /**
   * Near-bottom scroll handler that loads the next page, pre-built by the
   * container (SE-7100). Absent when there is no further page.
   */
  readonly onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

/**
 * Results body for the panel's persistent global search.
 * @param root0 - Component props.
 * @param root0.inPipeline - Matches already on this pipeline.
 * @param root0.recentlyViewed - Recently interacted matches.
 * @param root0.others - Every other match.
 * @param root0.matchCount - The ALL PLAYERS match count.
 * @param root0.isLoading - Whether results are loading.
 * @param root0.isEmpty - Whether the search matched nothing.
 * @param root0.error - Search error.
 * @param root0.addingPlayerIds - Ids with an add in flight.
 * @param root0.onAdd - Add callback.
 * @param root0.onScroll - Near-bottom scroll handler for the next page (SE-7100).
 */
const AddPlayersSearchResultsView = ({
  inPipeline,
  recentlyViewed,
  others,
  matchCount,
  isLoading,
  isEmpty,
  error,
  addingPlayerIds,
  onAdd,
  onScroll,
}: AddPlayersSearchResultsViewProps) =>
  isLoading ? (
    <Box className="flex-1 items-center justify-center p-4">
      <Spinner size="small" />
    </Box>
  ) : error ? (
    <Box className="flex-1 items-center justify-center p-4">
      <Text variant="caption" className="text-center text-status-error">
        {error}
      </Text>
    </Box>
  ) : isEmpty ? (
    <Box className="flex-1 items-center justify-center p-4">
      <Text variant="caption" className="text-center text-content-muted">
        No players match that search.
      </Text>
    </Box>
  ) : (
    <Stack className="min-h-0 flex-1 gap-2">
      <Text variant="caption" className="text-content-muted">
        Results across ALL players
      </Text>
      <ScrollView
        testID={TEST_ID_ADD_PLAYERS_PANEL.SEARCH_RESULTS}
        className={`flex-1 ${ifWeb("scrollbar-hide", "")}`.trim()}
        showsVerticalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={400}
      >
        {/* Same column geometry as the Suggested list this replaces: no
            horizontal padding (the drawer shell already insets by 20px) and a
            12px gap between groups. */}
        <Stack className="gap-3 pb-2">
          {renderResultGroup(
            "in-pipeline",
            "ALREADY IN THIS PIPELINE",
            inPipeline,
            true,
            addingPlayerIds,
            onAdd
          )}
          {renderResultGroup(
            "recently-viewed",
            "RECENTLY VIEWED",
            recentlyViewed,
            false,
            addingPlayerIds,
            onAdd
          )}
          {renderResultGroup(
            "all-players",
            `ALL PLAYERS · ${matchCount} MATCHES`,
            others,
            false,
            addingPlayerIds,
            onAdd
          )}
        </Stack>
      </ScrollView>
    </Stack>
  );

AddPlayersSearchResultsView.displayName = "AddPlayersSearchResultsView";

export default memo(AddPlayersSearchResultsView);
